import React, { useRef } from "react";
import { useDispatch } from "react-redux";
import { useCountsInTimeCoreFeedback } from "../../../../../hooks/Demo/feedbackTypes";
import { safeIterScore } from "../../../../../hooks/Demo/sharedLogic";
import {
  checkIfRightSide,
  checkIfSide,
} from "../../../../../utilities/Demo/angles";
import { loadTransferBetweenChairsAudio } from "../../../../../utilities/Demo/audioFeedback";
import {
  findAdjustedAngle,
  findDependentAngle,
} from "../../../../../utilities/Demo/physio";
import { checkArmsUsed } from "../../../../../utilities/Demo/Physio/basics";
import PoseDetection from "../../../PoseDetection";
import {
  backRomMetrics,
  computeMetrics,
  upperLegRomMetrics,
} from "../../../../../utilities/Demo/Physio/BergBalance/metrics";

export default function TransferBetweenChairs({
  onComplete,
  showWebcam = false,
}) {
  const dispatch = useDispatch();
  const leanedForward = useRef(false);
  const prePeaked = useRef(false);
  const isLeft = useRef(false);
  const stoodUp = useRef(false);
  const switchedSides = useRef(false);
  const armsUsed = useRef(false);
  const timeThresh = 60;
  const {
    correctCount,
    updateFeedback,
    updateCounts,
    seconds,
    playAudio,
    audioFeedback,
    setStartingTime,
    setSide,
    cptScore,
    finishedIntro,
  } = useCountsInTimeCoreFeedback({
    onComplete,
    timeThresh,
    loadAudio: loadTransferBetweenChairsAudio,
    maxScore: 1,
  });
  const metrics = useRef(upperLegRomMetrics().concat(backRomMetrics()));
  cptScore.current = () => {
    if (correctCount.current === 0) return 0;
    if (correctCount.current >= 1 && armsUsed.current) return 2 / 3;
    if (correctCount.current >= 1 && !armsUsed.current) return 1;
  };

  function observationStand(backAngle, kneeAngle, results) {
    if (kneeAngle < 100 && backAngle < 100) {
      armsUsed.current = false;
      prePeaked.current = false;
      leanedForward.current = false;
      stoodUp.current = false;
      updateFeedback("sitting");
    }
    if (backAngle > 100) {
      leanedForward.current = true;
    }
    // standing up
    if (kneeAngle > 120 && !prePeaked.current && leanedForward.current) {
      prePeaked.current = true;
      setStartingTime(seconds);
    } else if (prePeaked.current && kneeAngle > 160) {
      prePeaked.current = false;
      leanedForward.current = false;
      stoodUp.current = true;
      updateFeedback("standing");
    }

    if (checkArmsUsed(results, isLeft.current)) {
      updateFeedback("Do not use your arms!");
      armsUsed.current = true;
    }
  }

  function observationSit(backAngle, kneeAngle, results) {
    if (kneeAngle > 160) {
      prePeaked.current = false;
      leanedForward.current = false;
      updateFeedback("standing");
    }
    // sitting down
    if (kneeAngle > 120 && !prePeaked.current) {
      prePeaked.current = true;
    }
    if (backAngle > 100 && prePeaked.current) {
      leanedForward.current = true;
      updateFeedback("leaning forward");
    }
    if (
      kneeAngle < 100 &&
      backAngle < 100 &&
      leanedForward.current &&
      prePeaked.current
    ) {
      correctCount.current += 1;
      updateCounts(correctCount.current);
      const score = cptScore.current();
      playAudio(audioFeedback.current?.achievement, true);
      safeIterScore(dispatch, score);
      onComplete();
      seconds.current = timeThresh;
    }

    if (checkArmsUsed(results, isLeft.current)) {
      updateFeedback("Do not use your arms!");
      armsUsed.current = true;
    }
  }

  function onResults(results, canvasCtx) {
    if (!finishedIntro.current) return;

    if (!checkIfSide(results)) {
      playAudio(audioFeedback.current?.turnToSide, false, 6);
      updateFeedback("Turn to the side!");
      setSide(false);
      return;
    } else {
      updateFeedback("");
      setSide(true);
    }
    const isLeftObs = checkIfRightSide(results, "left");
    if (isLeftObs !== isLeft.current && stoodUp.current) {
      switchedSides.current = !switchedSides.current;
    }
    isLeft.current = isLeftObs;

    let kneeAngle;
    let backAngle;
    let hipKneeAngle;
    let displayConfigs;

    if (isLeft.current) {
      kneeAngle = findDependentAngle(
        results.poseLandmarks[23],
        results.poseLandmarks[25],
        results.poseLandmarks[27]
      );
      backAngle = findAdjustedAngle(
        results.poseLandmarks[11],
        results.poseLandmarks[23],
        180
      );
      hipKneeAngle =
        360 -
        findAdjustedAngle(results.poseLandmarks[23], results.poseLandmarks[25]);
      displayConfigs = {
        hipKneeAngle: {
          coord: results.poseLandmarks[23],
          left: false,
        },
        backAngle: {
          coord: results.poseLandmarks[11],
          left: false,
        },
      };
    } else {
      kneeAngle = findDependentAngle(
        results.poseLandmarks[24],
        results.poseLandmarks[26],
        results.poseLandmarks[28]
      );
      if (kneeAngle > 180) {
        kneeAngle = 360 - kneeAngle;
      }
      backAngle =
        360 -
        findAdjustedAngle(
          results.poseLandmarks[12],
          results.poseLandmarks[24],
          0
        );
      hipKneeAngle = findAdjustedAngle(
        results.poseLandmarks[24],
        results.poseLandmarks[26]
      );
      displayConfigs = {
        hipKneeAngle: {
          coord: results.poseLandmarks[24],
          left: false,
        },
        backAngle: {
          coord: results.poseLandmarks[12],
          left: false,
        },
      };
    }
    computeMetrics({
      metrics: metrics.current,
      angles: {
        current: {
          backAngle,
          hipKneeAngle,
        },
      },
      dispatch,
      displayConfigs,
      canvasCtx,
    });
    if (!stoodUp.current) {
      observationStand(backAngle, kneeAngle, results);
    } else {
      if (switchedSides.current) {
        observationSit(backAngle, kneeAngle, results);
      }
    }
  }

  return <PoseDetection onResults={onResults} showWebcam={showWebcam} />;
}
